import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import cn from "../../../utils/cn";
import { fieldProps, getPreferredProp } from "../utils";
import { Editor, EditorState, RichUtils } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { stateFromHTML } from 'draft-js-import-html';
import HtmlParsedText from "./components/HtmlParsedText";
import InlineStyleControls from "./components/InlineStyleControls";
import BlockStyleControls from "./components/BlockStyleControls";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getBlockStyle(block) {
  switch (block.getType()) {
    case 'blockquote':
      return 'RichEditor-blockquote';
    default:
      return null;
  }
}
const DraftEditorField = props => {
  const {
    label,
    value,
    id,
    dataTestId = '',
    hideError = false,
    hideLabel = false,
    inline = false,
    labelStyle = {},
    readOnly = false,
    readOnlyClass = null,
    required = false,
    spellCheck = true
  } = props;
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  let editorInstance = useRef(null);
  const focus = () => {
    editorInstance.current.focus();
  };
  useEffect(() => {
    setEditorState(EditorState.createWithContent(contentState));
  }, []);
  const currentValue = useRef(value);
  useEffect(() => {
    if (readOnly && currentValue.current !== value) {
      const contentState = stateFromHTML(nextValue);
      setEditorState(EditorState.createWithContent(contentState));
    }
  }, [value, readOnly]);
  const onChange = newEditorState => {
    // When the editor's content changes, we need to keep the editorState current
    // as well as the field's value.
    const changeCallback = getPreferredProp(props, 'onChange');
    if (changeCallback) {
      const value = stateToHTML(newEditorState.getCurrentContent());
      changeCallback(value);
    }
    setEditorState(newEditorState);
  };
  const toggleBlockType = blockType => {
    onChange(RichUtils.toggleBlockType(editorState, blockType));
  };
  const toggleInlineStyle = inlineStyle => {
    onChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };
  const handleKeyCommand = command => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      onChange(newState);
      return true;
    }
    return false;
  };
  const error = getPreferredProp(props, 'error') || (required ? 'Required' : '');
  const hasError = getPreferredProp(props, 'invalid') && getPreferredProp(props, 'touched');
  const containerClass = () => cn('font-regular-font', {
    flex: inline
  });
  const labelClass = () => cn('text-xs font-heavy-font tracking-wider', {
    'text-dark-grey': !hideError && !hasError,
    'text-red': !hideError && hasError,
    'sr-only top-0': hideLabel,
    'mr-4': inline,
    'app-components-form-field__label--required': required
  }, labelStyle);
  const richEditorClass = () => cn('bg-transparent', {
    'border border-solid border-light-border-blue rounded': !readOnly,
    'mt-1': !hideLabel,
    'read-only': readOnly
  });
  const contentState = editorState.getCurrentContent();
  const editorControls = /*#__PURE__*/_jsxs("div", {
    className: "grid grid-cols-6 text-sm",
    children: [/*#__PURE__*/_jsx("div", {
      className: "col-span-6 mb-1 select-none",
      children: /*#__PURE__*/_jsx(BlockStyleControls, {
        editorState: editorState,
        onToggle: toggleBlockType
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "col-end-12 col-span-3 mb-1 select-none",
      children: /*#__PURE__*/_jsx(InlineStyleControls, {
        editorState: editorState,
        onToggle: toggleInlineStyle
      })
    })]
  });
  if (readOnly) {
    return /*#__PURE__*/_jsxs("div", {
      className: "font-regular-font",
      children: [/*#__PURE__*/_jsx("label", {
        className: labelClass(),
        htmlFor: id,
        children: label
      }), /*#__PURE__*/_jsx(HtmlParsedText, {
        value: value,
        className: readOnlyClass
      })]
    });
  }
  ;
  return /*#__PURE__*/_jsxs("div", {
    className: containerClass(),
    children: [/*#__PURE__*/_jsx("label", {
      className: labelClass(),
      htmlFor: id,
      style: labelStyle,
      children: label
    }), /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsxs("div", {
        className: richEditorClass(),
        children: [/*#__PURE__*/_jsx("div", {
          className: "border-b border-solid border-light-border-blue rounded-t cursor-text min-h-100px hover:bg-medium-fill-grey",
          "data-testid": dataTestId,
          onClick: focus,
          role: "textbox",
          tabIndex: 0,
          id: id,
          children: /*#__PURE__*/_jsx("div", {
            className: "p-4 min-h-15px",
            children: /*#__PURE__*/_jsx(Editor, {
              ref: c => {
                editorInstance.current = c;
              },
              blockStyleFn: getBlockStyle,
              editorState: editorState,
              handleKeyCommand: handleKeyCommand,
              onChange: onChange,
              spellCheck: spellCheck,
              readOnly: readOnly,
              onBlur: getPreferredProp(props, 'onBlur'),
              onFocus: getPreferredProp(props, 'onFocus')
            })
          })
        }), !readOnly && editorControls]
      }), !hideError && hasError && /*#__PURE__*/_jsx("div", {
        className: "text-xs text-red",
        children: error
      })]
    })]
  });
};
DraftEditorField.propTypes = {
  dataTestId: PropTypes.string,
  hideError: PropTypes.bool,
  hideLabel: PropTypes.bool,
  id: PropTypes.string.isRequired,
  inline: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  labelStyle: PropTypes.object,
  readOnly: PropTypes.bool,
  readOnlyClass: PropTypes.string,
  required: PropTypes.bool,
  spellCheck: PropTypes.bool,
  value: PropTypes.string.isRequired,
  /** See fieldProps doc */
  ...fieldProps
};
export default DraftEditorField;
//# sourceMappingURL=DraftEditorField.js.map